import { V2, hoverOnly } from '../theme/tokens'
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Alert,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import TableFooterSimple from './common/TableFooterSimple';
import TableViewport from './common/TableViewport';
import EmptyState from './common/EmptyState';
import { getCanonicalTypeDisplayName } from '../utils/canonicalTypes';
import { BREW_FOCUS_LABELS, SKILL_LEVEL_LABELS, brewFocusLabel, skillLevelLabel } from '../utils/labels';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;
const ADMIN_HEADERS = ADMIN_KEY ? { headers: { 'X-Admin-Key': ADMIN_KEY } } : {};

const ROWS_PER_PAGE = 50;
const STATUS_OPTIONS = ['active', 'draft'];

// =====================================================================
// CANONICAL TYPE OPTIONS (50+ granular types)
// Aligned with backend/canonical_taxonomy.py CANONICAL_TYPES
// =====================================================================
const CANONICAL_TYPE_OPTIONS = [
  { value: '', label: 'No override (use inferred)', group: '' },

  // Electric Grinders
  { value: 'single_dose_espresso_grinder', label: 'Single Dose Espresso Grinder', group: 'Electric Grinders' },
  { value: 'single_dose_allrounder_grinder', label: 'Single Dose Allrounder Grinder', group: 'Electric Grinders' },
  { value: 'single_dose_filter_grinder', label: 'Single Dose Filter Grinder', group: 'Electric Grinders' },
  { value: 'hybrid_portable_grinder', label: 'Hybrid Portable Grinder', group: 'Electric Grinders' },
  { value: 'hopper_espresso_grinder', label: 'Hopper Espresso Grinder', group: 'Electric Grinders' },

  // Hand Grinders
  { value: 'hand_grinder_espresso', label: 'Hand Grinder (Espresso)', group: 'Hand Grinders' },
  { value: 'hand_grinder_filter', label: 'Hand Grinder (Filter)', group: 'Hand Grinders' },
  { value: 'hand_grinder_allrounder', label: 'Hand Grinder (Allrounder)', group: 'Hand Grinders' },

  // Espresso Machines
  { value: 'semi_auto_espresso_machine', label: 'Semi-Automatic Espresso Machine', group: 'Espresso Machines' },
  { value: 'lever_espresso_machine', label: 'Lever/Manual Espresso Machine', group: 'Espresso Machines' },
  { value: 'portable_espresso_machine', label: 'Portable Espresso Machine', group: 'Espresso Machines' },
  { value: 'automatic_espresso_machine', label: 'Automatic Espresso Machine', group: 'Espresso Machines' },
  { value: 'integrated_espresso_machine', label: 'Integrated Espresso Machine (with Grinder)', group: 'Espresso Machines' },

  // Grinder Parts & Burrs
  { value: 'burr_set', label: 'Replacement Burr Set', group: 'Grinder Parts' },
  { value: 'grinder_accessory', label: 'Grinder Accessory', group: 'Grinder Parts' },

  // Espresso Workflow Accessories
  { value: 'tamper', label: 'Espresso Tamper', group: 'Espresso Workflow' },
  { value: 'tamper_precision', label: 'Precision/Self-Leveling Tamper', group: 'Espresso Workflow' },
  { value: 'wdt_tool', label: 'WDT Distribution Tool', group: 'Espresso Workflow' },
  { value: 'distributor', label: 'Leveling Distributor', group: 'Espresso Workflow' },
  { value: 'tamping_station', label: 'Tamping Station', group: 'Espresso Workflow' },
  { value: 'tamping_mat', label: 'Tamping Mat', group: 'Espresso Workflow' },
  { value: 'knock_box', label: 'Knock Box/Drawer', group: 'Espresso Workflow' },
  { value: 'dosing_cup', label: 'Dosing Cup', group: 'Espresso Workflow' },
  { value: 'dosing_tube', label: 'Dosing Tube/Vial', group: 'Espresso Workflow' },

  // Portafilter Accessories
  { value: 'bottomless_portafilter', label: 'Bottomless Portafilter', group: 'Portafilter Accessories' },
  { value: 'precision_basket', label: 'Precision Filter Basket', group: 'Portafilter Accessories' },
  { value: 'portafilter_funnel', label: 'Portafilter Dosing Funnel', group: 'Portafilter Accessories' },

  // Milk & Latte Art
  { value: 'milk_pitcher', label: 'Milk Pitcher', group: 'Milk & Latte Art' },
  { value: 'milk_foamer', label: 'Electric Milk Foamer', group: 'Milk & Latte Art' },
  { value: 'thermometer', label: 'Thermometer', group: 'Milk & Latte Art' },

  // Measurement & Scales
  { value: 'coffee_scale', label: 'Coffee Scale', group: 'Measurement' },
  { value: 'coffee_scale_espresso', label: 'Espresso Scale (Compact)', group: 'Measurement' },

  // Brew Equipment
  { value: 'pour_over_dripper', label: 'Pour-Over Dripper', group: 'Brew Equipment' },
  { value: 'automatic_pour_over', label: 'Automatic Pour Over', group: 'Brew Equipment' },
  { value: 'vacuum_brewer', label: 'Vacuum/Siphon Brewer', group: 'Brew Equipment' },
  { value: 'integrated_filter_brewer', label: 'Integrated Filter Brewer (with Grinder)', group: 'Brew Equipment' },
  { value: 'kettle_gooseneck_electric', label: 'Electric Gooseneck Kettle', group: 'Brew Equipment' },
  { value: 'kettle_gooseneck_manual', label: 'Manual Gooseneck Kettle', group: 'Brew Equipment' },
  { value: 'glass_server', label: 'Glass Server/Carafe', group: 'Brew Equipment' },

  // Storage & Travel
  { value: 'travel_case', label: 'Equipment Travel Case', group: 'Storage & Travel' },
  { value: 'equipment_stand', label: 'Equipment Stand', group: 'Storage & Travel' },

  // Drinkware
  { value: 'drinkware', label: 'Drinkware/Mug', group: 'Drinkware' },
  { value: 'travel_mug', label: 'Travel Mug/Tumbler', group: 'Drinkware' },

  // Consumables
  { value: 'coffee_beans', label: 'Coffee Beans', group: 'Consumables' },
  { value: 'filter_paper', label: 'Filter Papers', group: 'Consumables' },
  { value: 'chai_tea', label: 'Chai/Tea', group: 'Consumables' },
  { value: 'cleaning_supplies', label: 'Cleaning Supplies', group: 'Consumables' },

  // Bundles & Other
  { value: 'bundle', label: 'Product Bundle', group: 'Bundles & Other' },
  { value: 'spare_part', label: 'Spare Part', group: 'Bundles & Other' },
  { value: 'gift_card', label: 'Gift Card', group: 'Bundles & Other' },
  { value: 'espresso_accessory', label: 'Espresso Accessory (General)', group: 'Bundles & Other' },
];

const SKILL_LEVEL_OPTIONS = [
  { value: '', label: 'No override' },
  { value: 'beginner', label: SKILL_LEVEL_LABELS.beginner },
  { value: 'intermediate', label: SKILL_LEVEL_LABELS.intermediate },
  { value: 'advanced', label: SKILL_LEVEL_LABELS.advanced },
  { value: 'pro', label: SKILL_LEVEL_LABELS.pro },
];

const BREW_FOCUS_OPTIONS = [
  { value: '', label: 'No override' },
  { value: 'espresso_only', label: BREW_FOCUS_LABELS.espresso_only },
  { value: 'espresso_primary', label: BREW_FOCUS_LABELS.espresso_primary },
  { value: 'allrounder', label: BREW_FOCUS_LABELS.allrounder },
  { value: 'filter_primary', label: BREW_FOCUS_LABELS.filter_primary },
  { value: 'filter_only', label: BREW_FOCUS_LABELS.filter_only },
];

const REVIEW_FILTER_OPTIONS = [
  { value: '', label: 'All products' },
  { value: 'needs_review', label: 'Needs review (no canonical type)' },
  { value: 'has_override', label: 'Has override' },
  { value: 'no_override', label: 'No override (using inferred)' },
];

const normalizeOverride = (override) => ({
  canonical_type: override?.canonical_type || '',
  skill_level: override?.skill_level || '',
  brew_focus: override?.brew_focus || '',
});

// Use centralized display name utility
const getCanonicalTypeLabel = getCanonicalTypeDisplayName;

export default function ProductsAttributesTable() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  // 🔴 Welle 3 PR 4 (§4.1): der Env-Name gehoert in den Tooltip, nicht in den
  // Nutzertext. Die Meldung sagt, was los ist; `title` traegt das Detail.
  const [errorTitle, setErrorTitle] = useState('');
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS);
  const [reviewFilter, setReviewFilter] = useState('');
  const [drafts, setDrafts] = useState({});
  const [, setSaving] = useState({});
  const [savedAt, setSavedAt] = useState({});
  // Zeilen-Fehler (D11): ein 409 `stufenware_erbt_taxonomie` darf nicht im
  // `console.error` verschwinden — er gehoert sichtbar an die Zeile.
  const [fehler, setFehler] = useState({});

  const fetchRows = useCallback(async () => {
    if (!ADMIN_KEY) {
      setItems([]);
      setTotal(0);
      setError('Admin actions are not configured in this build.');
      setErrorTitle('REACT_APP_ADMIN_KEY');
      return;
    }
    setLoading(true);
    setError('');
    setErrorTitle('');
    // R16 W3: ein Neuladen raeumt Zeilenfehler ab. Fuer Stufenware tritt der
    // Erfolgsfall nie ein, der rote Satz bliebe sonst fuer die ganze Sitzung.
    setFehler({});
    try {
      const params = {
        page: page + 1,
        page_size: ROWS_PER_PAGE,
      };
      if (search.trim()) params.q = search.trim();
      if (statusFilter.length) params.status = statusFilter.join(',');
      if (reviewFilter === 'needs_review') params.needs_canonical_type = 'true';
      if (reviewFilter === 'has_override') params.has_override = 'true';
      if (reviewFilter === 'no_override') params.no_override = 'true';

      const { data } = await axios.get(`${API_BASE}/api/admin/taxonomy/bulk`, {
        params,
        ...ADMIN_HEADERS,
      });
      setItems(data?.items || []);
      setTotal(Number(data?.total || 0));
      setDrafts((prev) => {
        const next = { ...prev };
        (data?.items || []).forEach((row) => {
          next[row.product_id] = normalizeOverride(row.override);
        });
        return next;
      });
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to load products.';
      setError(message);
      setErrorTitle('');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, reviewFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    setPage(0);
  }, [search, statusFilter, reviewFilter]);

  const handleDraftChange = (productId, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || {}),
        [field]: value,
      },
    }));
  };

  const handleSave = async (row, field, value) => {
    if (!ADMIN_KEY) return;
    const current = row.override?.[field];
    const same = value === (current ?? '');
    if (same) return;

    const rowId = row.product_id;
    setSaving((prev) => ({ ...prev, [rowId]: true }));

    try {
      // Note: This saves at product level, not variant level
      // To make it variant-specific, would need to pass variant_id and set scope: "variant"
      const payload = { product_id: rowId, [field]: value };
      const { data } = await axios.post(`${API_BASE}/api/admin/taxonomy/overrides`, payload, ADMIN_HEADERS);

      if (data?.override) {
        // Update items with new override data
        setItems((prev) =>
          prev.map((item) =>
            item.product_id === rowId ? { ...item, override: data.override } : item
          )
        );

        // Update drafts - merge with existing draft to preserve all fields
        setDrafts((prev) => {
          // Start with previous draft if it exists, otherwise normalize the old override
          const currentDraft = prev[rowId] || normalizeOverride(row.override);
          // Get the new override from backend
          const newOverride = normalizeOverride(data.override);

          // Merge: keep current draft values, but override with backend response for this specific field
          // This preserves any other pending changes while updating the saved field
          return {
            ...prev,
            [rowId]: {
              ...currentDraft, // Keep all existing draft fields
              [field]: newOverride[field] || value, // Use backend value, fallback to what user selected
            }
          };
        });

        // Fehler dieser Zeile ist erledigt.
        setFehler((prev) => {
          const next = { ...prev };
          delete next[rowId];
          return next;
        });

        // Show "Saved" indicator with animation
        setSavedAt((prev) => ({ ...prev, [rowId]: Date.now() }));

        // Clear "Saved" indicator after animation completes
        setTimeout(() => {
          setSavedAt((prev) => {
            const next = { ...prev };
            delete next[rowId];
            return next;
          });
        }, 2000);
      }
    } catch (err) {
      // Servertext bevorzugen (409 `stufenware_erbt_taxonomie` traegt `hinweis`);
      // der feste Satz bleibt Rueckfall (D11). R16 W3: in der Zelle steht ein
      // KURZER Satz, der volle Servertext nur im `title` — kein doppelter Text
      // und keine 173-px-Zeile.
      const data = err?.response?.data;
      const voll = data?.hinweis || data?.error || 'Failed to save override.';
      const kurz = data?.error === 'stufenware_erbt_taxonomie'
        ? 'Graded stock — edit the base product.'
        : 'Save failed — see details.';
      console.error('Failed to save override:', err);
      setFehler((prev) => ({ ...prev, [rowId]: { kurz, voll } }));
    } finally {
      setSaving((prev) => ({ ...prev, [rowId]: false }));
    }
  };

  const activeFilters = useMemo(() => {
    const out = [];
    if (search.trim()) out.push({ label: `Search: ${search.trim()}` });
    if (reviewFilter) {
      const opt = REVIEW_FILTER_OPTIONS.find((o) => o.value === reviewFilter);
      out.push({ label: opt?.label || reviewFilter });
    }
    if (statusFilter.length && statusFilter.length < STATUS_OPTIONS.length) {
      out.push({ label: `Status: ${statusFilter.join(', ')}` });
    }
    return out;
  }, [search, reviewFilter, statusFilter]);

  const clearFilters = () => {
    setSearch('');
    setReviewFilter('');
    setStatusFilter(STATUS_OPTIONS);
  };

  // Stats
  const needsReviewCount = useMemo(() => {
    return items.filter((row) => !row.taxonomy?.canonical_type && !row.override?.canonical_type).length;
  }, [items]);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">Canonical Type Editor</Typography>
            <Typography variant="body2" color="text.secondary">
              Set canonical product types for better recommendations. Focus on products that need review.
            </Typography>
          </Box>
          {error ? (
            <Alert severity="warning" sx={{ flex: 'none' }} title={errorTitle || undefined}>{error}</Alert>
          ) : null}
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            size="small"
            label="Search (SKU, name)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <TextField
            select
            size="small"
            label="Review status"
            value={reviewFilter}
            onChange={(e) => setReviewFilter(e.target.value)}
            sx={{ minWidth: 260 }}
          >
            {REVIEW_FILTER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Product status"
            value={statusFilter}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (selected?.length ? selected.join(', ') : 'None'),
            }}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Chip
            label="Clear filters"
            variant="outlined"
            onClick={clearFilters}
            sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
          />
        </Stack>

        {activeFilters.length ? (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {activeFilters.map((f) => (
              <Chip key={f.label} label={f.label} size="small" />
            ))}
          </Stack>
        ) : null}

        {needsReviewCount > 0 && !reviewFilter ? (
          <Alert
            severity="info"
            action={
              <Chip
                label="Show only"
                size="small"
                onClick={() => setReviewFilter('needs_review')}
                sx={{ cursor: 'pointer' }}
              />
            }
          >
            {needsReviewCount} product{needsReviewCount > 1 ? 's' : ''} on this page need{needsReviewCount === 1 ? 's' : ''} a canonical type.
          </Alert>
        ) : null}

        <Divider />

        {loading && <LinearProgress />}

        <TableViewport
          // Die Fusszeile steht AUSSERHALB des Kastens (nicht fixiert) — die
          // voreingestellten 80 px Bodenfreiheit waeren hier nur ein Loch.
          footerPadPx={0}
          // 🔴 Kein `maxHeight` mehr (Owner-Entscheid 2026-10-14, B3): die Tabelle
          // bekommt den Platz, den sie auf der Seite braucht, und die SEITE ist
          // der senkrechte Roller. Die Zeilenzahl begrenzt die Pagination (50),
          // nicht ein Deckel.
          //
          // 🔴 **Und hier KEIN `overflow: visible`** (Regel-16-Befund W3,
          // 2026-10-19, im Browser gemessen): die Tabelle traegt `minWidth: 900`,
          // der Kasten ist bei 1210–1280 px schmaler. Mit `visible` ginge der
          // Ueberlauf auf die Seite — gemessen bis **165 px** waagerechtes
          // Seitenrollen. Der Kasten rollt deshalb auf JEDER Breite selbst.
          //
          // Folge, und sie ist gewollt: der Kopf klebt auf dieser Flaeche nicht.
          // Der Grund ist immer derselbe — der NAECHSTGELEGENE Rollbehaelter ist
          // dieser Kasten selbst (`overflow: auto`, Hoehe = Inhalt), und der
          // scrollt senkrecht nie. Auf `/taxonomy` ist es kein anderer Grund:
          // der Reiter-Inhalt (`TaxonomyHub.jsx:263`) ist ein AEUSSERER
          // Behaelter und faengt Sticky nicht ab, er ermoeglicht es nur
          // relativ zu sich.
          // `TableViewport` setzt `overflow: auto` bereits als Vorgabe — der
          // Kasten braucht hier deshalb gar kein `sx`.
        >
          <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 280 }}>Product</TableCell>
                <TableCell sx={{ minWidth: 180 }}>Inferred Type</TableCell>
                <TableCell sx={{ minWidth: 260 }}>Canonical Type Override</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Skill Level</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Brew Focus</TableCell>
                <TableCell sx={{ width: 60 }} align="center">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    {/* §7.2 — der Grund gehoert in den Leerzustand, nicht in
                        einen Satz, der beide Faelle gleich behandelt.
                        „Kein Produkt hat diese Auswahl getroffen" und „es gibt
                        gar keine Produkte" haben verschiedene Auswege: das
                        eine ist ein Filter, das andere ein Sync. */}
                    {activeFilters.length > 0 ? (
                      <EmptyState
                        reason="no-match"
                        message="No products match those filters."
                        action={<Button size="small" variant="outlined" onClick={clearFilters}>Clear filters</Button>}
                      />
                    ) : (
                      <EmptyState
                        reason="no-data"
                        message="No products to classify yet."
                      />
                    )}
                  </TableCell>
                </TableRow>
              )}
              {items.map((row) => {
                const draft = drafts[row.product_id] || normalizeOverride(row.override);
                const savedAtRow = savedAt[row.product_id];
                const inferredType = row.taxonomy?.canonical_type;
                const hasType = inferredType || draft.canonical_type;

                return (
                  <TableRow
                    key={row.product_id}
                    hover
                    sx={{
                      position: 'relative',
                      bgcolor: !hasType ? 'warning.50' : undefined,
                      ...hoverOnly({ bgcolor: !hasType ? 'warning.100' : undefined }),
                      // Green fade animation when saved
                      ...(savedAtRow && {
                        animation: 'saveFade 2s ease-out',
                        '@keyframes saveFade': {
                          // 🔴 Der Blitz ist ein HINWEIS, keine Markierung. Der
                          //    Vorgaenger war `#4caf5020` — Alpha 0x20 ≈ 12,5 %.
                          //    `V2.okText` ist voll deckend und machte daraus
                          //    eine satte gruene Flaeche (R16-A W4). `okSoft` ist
                          //    die helle Token-Flaeche mit derselben Absicht.
                          '0%': { backgroundColor: V2.okSoft },
                          '100%': { backgroundColor: 'transparent' },
                        },
                      }),
                    }}
                  >
                    <TableCell sx={{ position: 'relative' }}>
                      {/* Sliding "Saved" text overlay */}
                      {savedAtRow && (
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'success.main',
                            fontWeight: 600,
                            fontSize: 11,
                            animation: 'slideIn 1s ease-out',
                            '@keyframes slideIn': {
                              '0%': { opacity: 0, transform: 'translate(-20px, -50%)' },
                              '20%': { opacity: 1, transform: 'translate(12px, -50%)' },
                              '80%': { opacity: 1, transform: 'translate(12px, -50%)' },
                              '100%': { opacity: 0, transform: 'translate(12px, -50%)' },
                            },
                          }}
                        >
                          Saved
                        </Typography>
                      )}
                      <Stack spacing={0.25}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {hasType ? (
                            <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                          ) : (
                            <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />
                          )}
                          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            {row.product_name || '—'}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          SKU {row.sku || '—'} {row.variant_name ? `· ${row.variant_name}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {row.product_status}
                        </Typography>
                        {fehler[row.product_id] ? (
                          <Typography
                            variant="caption"
                            color="error"
                            role="alert"
                            title={fehler[row.product_id].voll}
                          >
                            {fehler[row.product_id].kurz}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: inferredType ? 500 : 400 }}>
                          {getCanonicalTypeLabel(inferredType) || (
                            <Typography component="span" color="text.disabled">Not detected</Typography>
                          )}
                        </Typography>
                        {row.taxonomy?.skill_level && (
                          <Typography variant="caption" color="text.secondary">
                            {skillLevelLabel(row.taxonomy.skill_level)} · {brewFocusLabel(row.taxonomy.brew_focus, 'unknown')}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={draft.canonical_type || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleDraftChange(row.product_id, 'canonical_type', value);
                            handleSave(row, 'canonical_type', value);
                          }}
                          fullWidth
                          disabled={!ADMIN_KEY}
                          sx={{
                            '& .MuiSelect-select': {
                              py: 0.75,
                            },
                          }}
                        >
                          {CANONICAL_TYPE_OPTIONS.map((opt) => (
                            <MenuItem
                              key={opt.value || 'none'}
                              value={opt.value}
                              sx={{ fontSize: 12 }}
                            >
                              {opt.group ? (
                                <Typography component="span" color="text.secondary" sx={{ mr: 0.5 }}>
                                  [{opt.group}]
                                </Typography>
                              ) : null}
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                    </TableCell>

                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={draft.skill_level || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleDraftChange(row.product_id, 'skill_level', value);
                          handleSave(row, 'skill_level', value);
                        }}
                        fullWidth
                        disabled={!ADMIN_KEY}
                        sx={{
                          '& .MuiSelect-select': {
                            py: 0.75,
                          },
                        }}
                      >
                        {SKILL_LEVEL_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value || 'none'} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>

                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={draft.brew_focus || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleDraftChange(row.product_id, 'brew_focus', value);
                          handleSave(row, 'brew_focus', value);
                        }}
                        fullWidth
                        disabled={!ADMIN_KEY}
                        sx={{
                          '& .MuiSelect-select': {
                            py: 0.75,
                          },
                        }}
                      >
                        {BREW_FOCUS_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value || 'none'} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title="View taxonomy details">
                        <IconButton
                          component={RouterLink}
                          to={`/products/${row.sku}/taxonomy`}
                          size="small"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableViewport>

        <TableFooterSimple
          count={total}
          page={page}
          onPageChange={setPage}
          rowsPerPage={ROWS_PER_PAGE}
          leftSlot={`Total: ${total}`}
        />
      </Stack>
    </Paper>
  );
}